import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Briefcase,
  FileUp,
  Layers,
  Mail,
  Search,
  Send,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { listTeams, type TeamView } from '@/api/teams.api';
import { createEmployee, listDepartments, listEmployees, type EmployeeListItem } from '@/api/employees.api';
import { createInvitation, listInvitations } from '@/api/invitations.api';
import { ImportCsvModal } from '@/components/employees/ImportCsvModal';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Employees — Phase 3 Day 1.
 *
 * Multi-tenant directory scoped per organization. Anyone in the org can
 * read; visionary + integrator can add. Edit / soft-delete / RBAC-aware
 * visibility lands in subsequent Phase 3 days; this page intentionally
 * keeps a small, focused surface.
 *
 * The chrome's left-nav already pointed at /employees from Phase 1; this
 * page replaces the roadmap placeholder.
 */

const PAGE_SIZE = 100;

export function EmployeesPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const { show } = useToast();

  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>(''); // '' = all departments
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const canManage = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return ['visionary', 'integrator'].includes(authState.me.membership.role);
  }, [authState]);

  const queryArgs = useMemo(
    () => ({
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(departmentFilter ? { departmentId: departmentFilter } : {}),
      status: showInactive ? ('all' as const) : ('active' as const),
      limit: PAGE_SIZE,
    }),
    [search, departmentFilter, showInactive],
  );

  const employeesQuery = useQuery({
    queryKey: ['employees', queryArgs],
    queryFn: () => listEmployees(queryArgs),
  });
  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
  });
  // Teams are cross-cutting (ADR-047) — a person's department is their single
  // function, their teams are who they work with. Read is open to any member.
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => listTeams(),
  });
  // Invitation state powers the per-row "Invite" affordance. The list
  // endpoint is admin-only, so the query only runs for admins — everyone
  // else simply doesn't see the affordance.
  const invitationsQuery = useQuery({
    queryKey: ['invitations'],
    queryFn: listInvitations,
    enabled: canManage,
  });

  const pendingInviteEmails = useMemo(() => {
    const emails = new Set<string>();
    for (const inv of invitationsQuery.data ?? []) {
      if (inv.status === 'pending') emails.add(inv.email);
    }
    return emails;
  }, [invitationsQuery.data]);

  const inviteMutation = useMutation({
    mutationFn: (person: EmployeeListItem) =>
      createInvitation({
        email: person.email,
        departmentId: person.departmentId ?? undefined,
        employeeId: person.id,
      }),
    onSuccess: (_data, person) => {
      show(`Invitation sent to ${person.email}`, { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
    onError: (err) => {
      show(describeInviteError(err), { variant: 'danger' });
      // A 409 (already pending / already member) means our cache is
      // stale — refetch so the affordance corrects itself.
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  // Memoize to keep stable identities across renders so the
  // `groupedByDept` useMemo's dep list doesn't churn every render.
  const employees = useMemo(() => employeesQuery.data?.data ?? [], [employeesQuery.data]);
  const departments = useMemo(() => departmentsQuery.data ?? [], [departmentsQuery.data]);

  // Group by department for readability when no filter is active.
  // When a filter IS active, the user already chose a slice — show flat.
  const groupedByDept = useMemo(() => {
    if (departmentFilter || search.trim()) return null;
    const groups = new Map<string, EmployeeListItem[]>();
    const NO_DEPT = '__no_department__';
    for (const e of employees) {
      const key = e.departmentId ?? NO_DEPT;
      const list = groups.get(key) ?? [];
      list.push(e);
      groups.set(key, list);
    }
    // Build ordered groups based on departments query order.
    const ordered: { id: string; name: string; people: EmployeeListItem[] }[] = [];
    for (const dept of departments) {
      if (groups.has(dept.id)) {
        ordered.push({ id: dept.id, name: dept.name, people: groups.get(dept.id) ?? [] });
      }
    }
    if (groups.has(NO_DEPT)) {
      ordered.push({ id: NO_DEPT, name: 'No department', people: groups.get(NO_DEPT) ?? [] });
    }
    return ordered;
  }, [employees, departments, departmentFilter, search]);

  function invalidate(): Promise<void> {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['employees'] }),
      queryClient.invalidateQueries({ queryKey: ['departments'] }),
      queryClient.invalidateQueries({ queryKey: ['teams'] }),
    ]).then(() => undefined);
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Employees</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            {employeesQuery.data
              ? `${String(employeesQuery.data.total)} ${
                  employeesQuery.data.total === 1 ? 'person' : 'people'
                } in the directory`
              : 'Loading directory…'}
          </p>
        </div>
        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Link
              to="/settings/departments"
              className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] font-medium text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
            >
              <Layers className="size-4" />
              Manage departments
            </Link>
            <Button
              variant="secondary"
              onClick={() => {
                setImportOpen(true);
              }}
            >
              <FileUp className="size-4" />
              Import CSV
            </Button>
            <Button
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              <UserPlus className="size-4" />
              Add employee
            </Button>
          </div>
        ) : null}
      </div>

      {/* Filters */}
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center">
        <label className="flex flex-1 items-center gap-2 rounded-kw-md border border-kw-border bg-white px-3 py-1.5 text-[13px] focus-within:border-kw-cerulean focus-within:ring-2 focus-within:ring-kw-cerulean/20">
          <Search className="size-4 text-kw-muted" />
          <input
            type="text"
            placeholder="Search name, email, or title…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            className="flex-1 bg-transparent text-kw-shark placeholder:text-kw-muted focus:outline-none"
          />
          {search ? (
            <button
              type="button"
              onClick={() => {
                setSearch('');
              }}
              aria-label="Clear search"
              className="text-kw-muted hover:text-kw-shark"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </label>
        <select
          value={departmentFilter}
          onChange={(e) => {
            setDepartmentFilter(e.target.value);
          }}
          aria-label="Filter by department"
          className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} ({String(d.employeeCount)})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-[12px] text-kw-muted">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
            }}
            className="size-4 accent-kw-cerulean"
          />
          Show inactive
        </label>
      </Card>

      {/* Body */}
      {employeesQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : employeesQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load employees</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {employeesQuery.error instanceof Error ? employeesQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : employees.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Users className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">
              {search || departmentFilter ? 'No matches' : 'No employees yet'}
            </p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {search || departmentFilter
                ? 'Try clearing your filters or broadening the search.'
                : canManage
                  ? 'Add your first employee to start the directory.'
                  : 'An admin can add the first employee.'}
            </p>
          </div>
        </Card>
      ) : groupedByDept ? (
        <div className="space-y-4">
          {groupedByDept.map((group) => (
            <DepartmentSection
              key={group.id}
              title={group.name}
              people={group.people}
              canManage={canManage}
              pendingInviteEmails={pendingInviteEmails}
              onInvite={(person) => {
                inviteMutation.mutate(person);
              }}
              invitingId={inviteMutation.isPending ? inviteMutation.variables.id : null}
            />
          ))}
        </div>
      ) : (
        <DepartmentSection
          title="Results"
          people={employees}
          canManage={canManage}
          pendingInviteEmails={pendingInviteEmails}
          onInvite={(person) => {
            inviteMutation.mutate(person);
          }}
          invitingId={inviteMutation.isPending ? inviteMutation.variables.id : null}
        />
      )}

      <CreateEmployeeModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        departments={departments}
        teams={teamsQuery.data ?? []}
        onCreated={() => {
          void invalidate();
        }}
      />

      <ImportCsvModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
        onImported={() => {
          void invalidate();
          void queryClient.invalidateQueries({ queryKey: ['invitations'] });
        }}
      />
    </div>
  );
}

interface RowInviteProps {
  canManage: boolean;
  pendingInviteEmails: Set<string>;
  onInvite: (person: EmployeeListItem) => void;
  /** Employee id currently being invited (disables that row's button). */
  invitingId: string | null;
}

function DepartmentSection({
  title,
  people,
  canManage,
  pendingInviteEmails,
  onInvite,
  invitingId,
}: { title: string; people: EmployeeListItem[] } & RowInviteProps) {
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
        {title}
        <span className="font-normal text-kw-ghost">({people.length})</span>
      </h2>
      <Card className="p-0">
        <ul className="divide-y divide-kw-border">
          {people.map((p) => (
            <EmployeeRow
              key={p.id}
              person={p}
              canManage={canManage}
              pendingInviteEmails={pendingInviteEmails}
              onInvite={onInvite}
              invitingId={invitingId}
            />
          ))}
        </ul>
      </Card>
    </section>
  );
}

function EmployeeRow({
  person,
  canManage,
  pendingInviteEmails,
  onInvite,
  invitingId,
}: { person: EmployeeListItem } & RowInviteProps) {
  const initials = `${person.firstName.charAt(0)}${person.lastName.charAt(0)}`.toUpperCase() || '·';
  // Invite affordance: admin-only, and only for directory rows without a
  // linked portal account. A live pending invitation shows as a badge
  // instead of the button.
  const hasPendingInvite = pendingInviteEmails.has(person.email.toLowerCase());
  const showInviteButton = canManage && !person.membershipId && !hasPendingInvite;
  return (
    // The invite button sits OUTSIDE the Link (absolutely positioned) so
    // we never nest interactive content inside the anchor.
    <li className="relative">
      {/* The whole row is a link to the profile. The mailto anchor
          uses e.stopPropagation so clicking the email opens the
          mail client without navigating away from the directory. */}
      <Link
        to={`/employees/${person.id}`}
        className="flex items-start gap-3 p-4 pr-24 transition-colors hover:bg-kw-bg-blue"
      >
        <div className="grid size-10 shrink-0 place-items-center rounded-kw-full bg-kw-cerulean text-[13px] font-medium text-white">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <p className="text-[14px] font-medium text-kw-shark">
              {person.firstName} {person.lastName}
            </p>
            {!person.isActive ? <Badge variant="neutral">Inactive</Badge> : null}
            {person.membershipId ? null : hasPendingInvite ? (
              <Badge variant="info">Invite pending</Badge>
            ) : (
              <span
                className="text-[10px] uppercase tracking-wide text-kw-ghost"
                title="No portal account yet"
              >
                Directory only
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-kw-muted">
            {person.jobTitle ? (
              <span className="inline-flex items-center gap-1">
                <Briefcase className="size-3" />
                {person.jobTitle}
              </span>
            ) : null}
            {/* Email is plain text in the row to avoid nested <a>; the
                profile page renders it as a mailto anchor. */}
            <span className="inline-flex items-center gap-1">
              <Mail className="size-3" />
              {person.email}
            </span>
            {person.manager ? (
              <span className="text-kw-ghost">
                Reports to {person.manager.firstName} {person.manager.lastName}
              </span>
            ) : null}
          </div>
          {person.teams.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {person.teams.map((t) => (
                <Badge key={t.id} variant={t.kind === 'leadership' ? 'info' : 'neutral'}>
                  {t.name}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      </Link>
      {showInviteButton ? (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={invitingId === person.id}
            isLoading={invitingId === person.id}
            onClick={() => {
              onInvite(person);
            }}
            title={`Send a portal invitation to ${person.email}`}
          >
            <Send className="size-3.5" />
            Invite
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** Map invitation error codes to plain-English toast copy. */
function describeInviteError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'invitation_pending') return 'An invitation is already pending for this email.';
    if (code === 'membership_exists') return 'This person is already a member of the org.';
    if (code === 'role_not_allowed') return 'That role cannot be invited.';
  }
  return err instanceof Error ? err.message : 'Could not send the invitation';
}

interface DepartmentOption {
  id: string;
  name: string;
}

function CreateEmployeeModal({
  open,
  onClose,
  departments,
  teams,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  departments: DepartmentOption[];
  teams: TeamView[];
  onCreated: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);

  function toggleTeam(id: string): void {
    setTeamIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  }

  const mutation = useMutation({
    mutationFn: () =>
      createEmployee({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        jobTitle: jobTitle.trim() || undefined,
        departmentId: departmentId || undefined,
        hireDate: hireDate || undefined,
        teamIds: teamIds.length > 0 ? teamIds : undefined,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      // Reset for the next add.
      setFirstName('');
      setLastName('');
      setEmail('');
      setJobTitle('');
      setDepartmentId('');
      setHireDate('');
      setTeamIds([]);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add employee" size="md">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <p className="text-[13px] text-kw-muted">
          Adds a row to the directory. The person doesn&apos;t need a portal account yet — when they sign in
          via magic link with this email, the membership and directory record link automatically.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="First name"
            name="firstName"
            required
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
            }}
          />
          <Input
            label="Last name"
            name="lastName"
            required
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
          />
        </div>

        <Input
          label="Work email"
          name="email"
          type="email"
          required
          autoComplete="off"
          placeholder="first.last@kaerwell.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />

        <Input
          label="Job title (optional)"
          name="jobTitle"
          placeholder="e.g. Operations Lead"
          value={jobTitle}
          onChange={(e) => {
            setJobTitle(e.target.value);
          }}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-kw-muted">Department (optional)</span>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
              }}
              className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            >
              <option value="">No department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Hire date (optional)"
            name="hireDate"
            type="date"
            value={hireDate}
            onChange={(e) => {
              setHireDate(e.target.value);
            }}
          />
        </div>

        {teams.length > 0 ? (
          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-[12px] font-medium text-kw-muted">Teams (optional)</legend>
            <p className="text-[12px] text-kw-muted">
              Separate from their department — someone can run one function and still sit on the leadership
              team.
            </p>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {teams.map((t) => {
                const selected = teamIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      toggleTeam(t.id);
                    }}
                    className={[
                      'rounded-kw-md border px-2.5 py-1 text-[12px] transition-colors',
                      selected
                        ? 'border-kw-cerulean-dark bg-kw-cerulean-dark text-white'
                        : 'border-kw-border bg-white text-kw-shark hover:bg-kw-bg',
                    ].join(' ')}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ) : null}

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{describeMutationError(mutation.error)}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={mutation.isPending}
            disabled={!firstName.trim() || !lastName.trim() || !email.trim()}
          >
            Add employee
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Map known server error codes to plain-English copy. The route returns
 * `{ error: 'email_taken' | 'department_not_found' | … }` JSON for the
 * specific business-rule failures; everything else falls back to the
 * generic message.
 */
function describeMutationError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'email_taken') return 'Someone with this email is already in the directory.';
    if (code === 'department_not_found') return 'That department was not found in your org.';
    if (code === 'manager_not_found') return 'That manager was not found in your org.';
    if (code === 'validation_failed') return 'Some fields look invalid — double-check and retry.';
  }
  return err instanceof Error ? err.message : 'Could not add employee';
}
